import { ISPListSchema } from '@ms/odsp-datasources/lib/dataSources/item/spListItemRetriever/interfaces/ISPGetItemResponse';
import { decodeText } from '@ms/utilities-encoding/lib/XmlEncoding';
import { IExpression } from '@ms/utilities-custom-formatter/lib/ICustomFormatter';

/**
 * Schema of the JSON blob that comes to us from the CustomFormatter
 * property of ViewXML.
 */
export interface IViewFormatter {
    /**
     * The cardFormatter JSON string that defines the format of the card.
     * The JSON is in the format defined by
     * ICustomFormatter
     */
    cardFormatter?: string;

    /**
     * The rowFormatter JSON string that defines the format of the row.
     * The JSON is in the format defined by
     * ICustomFormatter
     */
    rowFormatter?: string;

    /**
     * A string that represents JSON in the format of IExpression. The output
     * of the expression (after being evaluated by CustomFormatter) is a string
     * that represents the class to be applied to a row.
     */
    additionalRowClass?: string;

    /**
     * If true, we'll use it as a signal to hide row selection UX
     */
    hideSelection?: boolean;

    /**
     * If true, we'll use it as a signal to hide the list header.
     */
    hideColumnHeader?: boolean;
    /**
     * Props for formatting the card in tileslist.
     */
    cardProps?: ICardProps;
    /**
     * If true, we'll use it as a signal to hide the list header.
     * Deprecated... Do not use. Use hideColumnHeader instead.
     */
    hideListHeader?: boolean;

    /**
     * A string that denotes the Template Identifier used to create this formatter JSON
     */
    templateId?: string;
}

export interface ICardProps {
    height: number;
    width: number;
}

/**
 * The itemCache will store a facet that contains the viewFormatter class below.
 */
export interface IViewFormatterFacet {
    viewFormatter: ViewFormatter;
}

/**
 * This is a class that objectifies the ISPListSchema.CustomFormatter property
 * of a view. ISPListSchema.CustomFormatter is a string that represents a JSON
 * blob with schema IViewFormatter (above).
 */
export class ViewFormatter {
    private _viewFormatter: IViewFormatter;
    private _rowFormatStr: string | undefined;
    private _cardFormatStr: string | undefined;

    constructor(viewRendererStringRaw: string) {
        // The viewRendererString is xml encoded, so unencode it now...
        let viewRendererString = decodeText(viewRendererStringRaw);
        this._viewFormatter = JSON.parse(viewRendererString);
        if (this._viewFormatter.rowFormatter) {
            this._rowFormatStr = JSON.stringify(this._viewFormatter.rowFormatter);
            this._cardFormatStr = JSON.stringify(this._viewFormatter.rowFormatter);
        }
        if (this._viewFormatter.cardFormatter) {
            this._cardFormatStr = JSON.stringify(this._viewFormatter.cardFormatter);
        }
    }

    /**
     * Returns true if IViewFormatterJson has specified that we
     * want to hide the list header
     */
    public shouldHideHeader(): boolean {
        let viewFormatter = this._viewFormatter;
        // We are deprecating hideListHeader. But don't remove it from code since there are
        // solutions that use it.
        return (Boolean(viewFormatter.hideListHeader) || Boolean(viewFormatter.hideColumnHeader));
    }

    public getCardProps(): ICardProps | undefined {
        let viewFormatter = this._viewFormatter;
        if (!viewFormatter.cardProps) {
            return undefined;
        }
        return viewFormatter.cardProps;
    }

    /**
     * Returns true if IViewFormatterJson has specified that we
     * want to hide list item selection
     */
    public shouldHideSelection(): boolean {
        let viewFormatter = this._viewFormatter;
        return Boolean(viewFormatter.hideSelection);
    }
    /**
     * Returns the RowFormatter string, since at this point, the CustomFormatter
     * module input is a string
     */
    public getRowFormatterStr(): string | undefined {
        return this._rowFormatStr;
    }

    /**
     * Returns the CardFormatter string, since at this point, the CustomFormatter
     * module input is a string
     */
    public getCardFormatterStr(): string | undefined {
        return this._cardFormatStr;
    }

    /**
     * Returns the JSON CustomFormatter Expression that will define
     * what class will be applied to the row. This can be used to specify
     * conditional formatting on the entire row.
     */
    public getAdditionalRowClass(): string | IExpression | undefined {
        return this._viewFormatter.additionalRowClass;
    }

    /**
     * Returns the Template Identifier used to create the formatter JSON.
     */
    public getTemplateId(): string | undefined {
        return this._viewFormatter.templateId;
    }
}

/**
 *
 * A helper function to create the above CustomFormatter object that we can then
 * cache in the item cache, so that it doesn't have to be created again and again.
 * This function returns an object with schema that looks like {viewFormatter : CustomFormatter}
 */
export function getViewFormatterFacetObj(listSchema: ISPListSchema | null | undefined): IViewFormatterFacet | undefined {
    return getViewFormatterFacetObjFromString(listSchema && listSchema.CustomFormatter);
}

export function getViewFormatterFacetObjFromString(formatter: string | null | undefined): IViewFormatterFacet | undefined {
    let viewFormatterRet: IViewFormatterFacet;

    try {
        // Within try-catch since the ViewFormatter constructor can throw if
        // the JSON is invalid etc. If so, we return undefined which means we'll
        // treat the view as if it doesn't have a view formatter.
        if (formatter) {
            let viewFormatterObj = new ViewFormatter(formatter);
            viewFormatterRet = ({ viewFormatter: viewFormatterObj });
            return viewFormatterRet;
        }
    } catch (e) {
        // Ignore errors we just return null.
    }

    // If somethign goes wrong, the function returns undefined
}

/**
 * Does this list view have column formatters defined on it?
 * This will help us deterime whether to wait for the customFormatter
 * module to load before we begin to render the list view..
 * @param listSchema
 */
export function hasColumnFormatters(listSchema: ISPListSchema | undefined): boolean {
    let hasFormatter = false;
    if (listSchema && listSchema.Field) {
        let fields = listSchema.Field;
        for (let i = 0; i < fields.length; i++) {
            let field = fields[i];
            if (field.CustomFormatter) {
                hasFormatter = true;
                break;
            }
        }
    }
    return hasFormatter;
}
